import React, { createContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { contentMap } from '../config/contentMap'; // Import from the new file
import { seoContent } from '../config/contentSEO'; // Import from the new file
import {
  DEFAULT_DAILY_RATE,
  DEFAULT_MONTHLY_DISPLAY_RATE,
  DEFAULT_NO_CONTRACT_MONTHLY_RATE,
  calculateWeeklyFromDaily,
} from '../utils/pricing';
import { todayEST, addDays } from '../utils/dateLite';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [addresses, setAddresses] = useState([]); // Fetch addresses from MongoDB
  const [isAddressesLoading, setIsAddressesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState(null); // null = all states, or 'NC', 'GA', etc.
  const [defaultRates, setDefaultRates] = useState({
    dailyRate: DEFAULT_DAILY_RATE,
    weeklyRate: calculateWeeklyFromDaily(DEFAULT_DAILY_RATE), // Auto-calculated with 4.8% savings
    monthlyRateOH: DEFAULT_MONTHLY_DISPLAY_RATE,
    monthlyRate: 252,
    noContractRate: DEFAULT_NO_CONTRACT_MONTHLY_RATE,
    contractRate: true,
  });

  const [availableLots, setAvailableLots] = useState([]);
  const [gateCode, setGateCode] = useState('0000');
  const [bookingDetails, setBookingDetails] = useState({});
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [showPricingCards, setShowPricingCards] = useState(false);
  const [showStateCards, setShowStateCards] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSource, setBookingSource] = useState(null); // Track where booking was initiated from
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [lotsAvailableByLocationId, setLotsAvailableByLocationId] = useState({});
  const [lotsBookedByLocationId, setLotsBookedByLocationId] = useState({});
  const [filteredAddresses, setFilteredAddresses] = useState([]);
  const [message, setMessage] = useState('');
  const [isInitialLotsLoading, setIsInitialLotsLoading] = useState(false);
  const [isRefreshingLots, setIsRefreshingLots] = useState(false);
  const [content, setContent] = useState('home');
  const [title, setTitle] = useState('Secure & Flexible Parking');
  const initialLoadsInFlightRef = useRef(0);
  const refreshLoadsInFlightRef = useRef(0);

  const pricingOptions = [
    { price: String(DEFAULT_DAILY_RATE), duration: "per day" },
    { price: String(calculateWeeklyFromDaily(DEFAULT_DAILY_RATE)), duration: "per week" },
    { price: String(defaultRates.noContractRate), duration: "no contract" },
    { price: String(defaultRates.monthlyRateOH), duration: "per month" },
  ];

  // Fetch addresses from MongoDB
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await axios.get('/api/addresses', {
          params: {
            limit: 100,  // Set a high limit to get all addresses
          }
        });
        if (response.data.success) {
          setAddresses(response.data.addresses);
        } else {
          console.error('Failed to fetch addresses:', response.data.error);
        }
      } catch (error) {
        console.error('Failed to fetch addresses:', error.response?.data?.message || error.message);
      } finally {
        setIsAddressesLoading(false);
      }
    };

    fetchAddresses();
  }, []);

  useEffect(() => {
    const q = (searchQuery || '').trim().toLowerCase();

    const filtered = addresses
      // Keep monthly-only locations visible in the client-facing locations flow
      // even when they are not available for daily booking in-app.
      .filter(address => address.availability || address.openForMonthly === true)
      .filter(addr => {
        // State filter
        if (stateFilter) {
          const addressText = (addr.address || '');
          const parts = addressText.split(',').map(s => (s || '').trim());
          const stateZip = parts[parts.length - 1] || '';
          const stateAbbrev = (stateZip.split(' ')[0] || '').toUpperCase();
          if (stateAbbrev !== stateFilter) return false;
        }
        return true;
      })
      .filter(addr => {
        // Text search filter
        if (!q) return true;
        const addressText = (addr.address || '').toLowerCase();
        const parts = addressText.split(',').map(s => (s || '').trim());
        const streetLine = parts[0] || addressText; // everything before first comma
        const city = parts[1] || '';
        const stateZip = parts[2] || parts[1] || ''; // some addresses may be "..., NC 28269" as second or third part
        const stateAbbrev = (stateZip.split(' ')[0] || '').toLowerCase();

        // Build word-boundary regex for street prefix matching only
        const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const streetPrefix = new RegExp(`\\b${escapeRegex(q)}`);

        const matchesStreet = streetPrefix.test(streetLine);
        const matchesState = stateAbbrev.startsWith(q);
        const matchesCity = q.length >= 3 ? streetPrefix.test(city) : false; // enable city match for >=3 chars
        return matchesStreet || matchesState || matchesCity;
      });
    setFilteredAddresses(filtered);
  }, [addresses, searchQuery, stateFilter, lotsAvailableByLocationId]);

  // Fetch lots once when filteredAddresses become available (avoids race with addresses effect)
  const hasFetchedLotsRef = useRef(false);

  // Fetch bays and compute "bookable tonight" availability (today -> tomorrow)
  // `locationId` narrows refresh to one location; omitted means global refresh.
  const retrieveLots = useCallback(async ({ background = false, locationId = null } = {}) => {
    const createEmptyLotsState = () => {
      const emptyState = {};
      addresses.forEach(address => {
        emptyState[address.id] = [];
      });
      return emptyState;
    };

    const dedupeBaysByLotId = (bays) => {
      const byLotId = new Map();
      bays.forEach((bay) => {
        if (!bay?.lotId) return;
        if (!byLotId.has(bay.lotId)) {
          byLotId.set(bay.lotId, bay);
        }
      });
      return [...byLotId.values()];
    };

    const splitLocationBays = (locationBays, availableLots) => {
      if (!Array.isArray(locationBays) || locationBays.length === 0) {
        return {
          available: Array.isArray(availableLots) ? availableLots : [],
          booked: [],
        };
      }

      const availableLotIds = new Set(
        (Array.isArray(availableLots) ? availableLots : [])
          .map(lot => lot?.lotId)
          .filter(Boolean)
      );

      return {
        available: locationBays.filter(bay => availableLotIds.has(bay.lotId)),
        booked: locationBays.filter(bay => !availableLotIds.has(bay.lotId)),
      };
    };

    if (background) {
      refreshLoadsInFlightRef.current += 1;
      setIsRefreshingLots(true);
    } else {
      initialLoadsInFlightRef.current += 1;
      setIsInitialLotsLoading(true);
    }

    const newAvailable = createEmptyLotsState();
    const newBooked = createEmptyLotsState();
    const checkInDate = todayEST();
    const checkOutDate = addDays(checkInDate, 1);

    try {
      if (locationId) {
        const currentAvailableForLocation = lotsAvailableByLocationId?.[locationId] || [];
        const currentBookedForLocation = lotsBookedByLocationId?.[locationId] || [];
        let locationBays = dedupeBaysByLotId([...currentAvailableForLocation, ...currentBookedForLocation]);

        if (locationBays.length === 0) {
          try {
            const catalogResponse = await axios.get('/api/catalog');
            if (catalogResponse?.data?.success && Array.isArray(catalogResponse.data.objects)) {
              locationBays = catalogResponse.data.objects.filter(bay => bay?.locationId === locationId);
            } else {
              locationBays = [];
            }
          } catch (catalogError) {
            console.warn(`Could not fetch catalog for location ${locationId}:`, catalogError);
          }
        }

        try {
          const availableResponse = await axios.post('/api/available-lots', {
            startDate: checkInDate,
            endDate: checkOutDate,
            locationId,
          });

          const availableLots = availableResponse?.data?.success && Array.isArray(availableResponse.data.objects)
            ? availableResponse.data.objects
            : [];
          const { available, booked } = splitLocationBays(locationBays, availableLots);

          setLotsAvailableByLocationId(prev => ({ ...prev, [locationId]: available }));
          setLotsBookedByLocationId(prev => ({ ...prev, [locationId]: booked }));
          return {
            success: true,
            availableByLocationId: { [locationId]: available },
            bookedByLocationId: { [locationId]: booked },
          };
        } catch (availableLotsError) {
          if (locationBays.length > 0) {
            const fallbackAvailable = locationBays.filter(bay => bay.availabilityStatus === 'Available');
            const fallbackBooked = locationBays.filter(bay => bay.availabilityStatus !== 'Available');
            setLotsAvailableByLocationId(prev => ({ ...prev, [locationId]: fallbackAvailable }));
            setLotsBookedByLocationId(prev => ({ ...prev, [locationId]: fallbackBooked }));
            return {
              success: true,
              availableByLocationId: { [locationId]: fallbackAvailable },
              bookedByLocationId: { [locationId]: fallbackBooked },
            };
          }

          console.warn(`Could not refresh lots for location ${locationId}; leaving current state unchanged.`, availableLotsError);
          return {
            success: false,
            availableByLocationId: { [locationId]: currentAvailableForLocation },
            bookedByLocationId: { [locationId]: currentBookedForLocation },
          };
        }
      }

      // Get all active bays once, then evaluate "bookable tonight" per location.
      const response = await axios.get('/api/catalog');
      if (!response.data.success || !Array.isArray(response.data.objects)) {
        setLotsAvailableByLocationId(newAvailable);
        setLotsBookedByLocationId(newBooked);
        return { success: false, availableByLocationId: newAvailable, bookedByLocationId: newBooked };
      }

      const allBays = response.data.objects;
      const baysByLocation = {};

      allBays.forEach(bay => {
        const locationId = bay.locationId;
        if (!locationId) return;

        if (!baysByLocation[locationId]) baysByLocation[locationId] = [];
        baysByLocation[locationId].push(bay);

        if (!newAvailable[locationId]) newAvailable[locationId] = [];
        if (!newBooked[locationId]) newBooked[locationId] = [];
      });

      const locationIds = [...new Set([...addresses.map(address => address.id), ...Object.keys(baysByLocation)])].filter(Boolean);

      if (locationIds.length === 0) {
        setLotsAvailableByLocationId(newAvailable);
        setLotsBookedByLocationId(newBooked);
        return { success: true, availableByLocationId: newAvailable, bookedByLocationId: newBooked };
      }

      let batchAvailabilityByLocationId = {};
      let didBatchRequestFail = false;

      try {
        const availableBatchResponse = await axios.post('/api/available-lots/batch', {
          startDate: checkInDate,
          endDate: checkOutDate,
          locationIds,
        });

        if (
          availableBatchResponse?.data?.success
          && availableBatchResponse.data.objectsByLocationId
          && typeof availableBatchResponse.data.objectsByLocationId === 'object'
        ) {
          batchAvailabilityByLocationId = availableBatchResponse.data.objectsByLocationId;
        } else {
          didBatchRequestFail = true;
        }
      } catch (error) {
        didBatchRequestFail = true;
      }

      locationIds.forEach((locationId) => {
        const locationBays = baysByLocation[locationId] || [];
        const availableLots = batchAvailabilityByLocationId[locationId];

        if (didBatchRequestFail || !Array.isArray(availableLots)) {
          newAvailable[locationId] = locationBays.filter(bay => bay.availabilityStatus === 'Available');
          newBooked[locationId] = locationBays.filter(bay => bay.availabilityStatus !== 'Available');
          return;
        }

        // If catalog has no bays for this location, trust the available-lots response directly.
        if (locationBays.length === 0) {
          newAvailable[locationId] = availableLots;
          newBooked[locationId] = [];
          return;
        }

        const availableLotIds = new Set(
          availableLots
            .map(lot => lot?.lotId)
            .filter(Boolean)
        );

        newAvailable[locationId] = locationBays.filter(bay => availableLotIds.has(bay.lotId));
        newBooked[locationId] = locationBays.filter(bay => !availableLotIds.has(bay.lotId));
      });

      setLotsAvailableByLocationId(newAvailable);
      setLotsBookedByLocationId(newBooked);
      return { success: true, availableByLocationId: newAvailable, bookedByLocationId: newBooked };

    } catch (error) { 
      // Initialize empty arrays for all locations on error
      const emptyAvailable = createEmptyLotsState();
      const emptyBooked = createEmptyLotsState();
      setLotsAvailableByLocationId(emptyAvailable);
      setLotsBookedByLocationId(emptyBooked);
      return { success: false, availableByLocationId: emptyAvailable, bookedByLocationId: emptyBooked };
    } finally { 
      if (background) {
        refreshLoadsInFlightRef.current = Math.max(0, refreshLoadsInFlightRef.current - 1);
        if (refreshLoadsInFlightRef.current === 0) {
          setIsRefreshingLots(false);
        }
      } else {
        initialLoadsInFlightRef.current = Math.max(0, initialLoadsInFlightRef.current - 1);
        if (initialLoadsInFlightRef.current === 0) {
          setIsInitialLotsLoading(false);
        }
      }
    }
  }, [addresses, lotsAvailableByLocationId, lotsBookedByLocationId]);

  useEffect(() => {
    if (!hasFetchedLotsRef.current && Array.isArray(filteredAddresses) && filteredAddresses.length > 0) {
      hasFetchedLotsRef.current = true;
      retrieveLots();
    }
  }, [filteredAddresses, retrieveLots]);

  const getLocationWithRates = (locationId) => {
    const location = addresses.find(addr => addr.id === locationId);
    if (!location) return null;
    return { ...defaultRates, ...location };
  };

  const getAllLocationsWithRates = () => {
    return addresses.map(location => ({
      ...defaultRates,
      ...location
    }));
  };

  const filterLotsByLocationId = (locationId, lotsById) => {
    try {
      const availableLotsForLocation = lotsById[locationId] || [];
      const randomLot = getRandomAvailableLot(availableLotsForLocation);
      
      if (!randomLot) {
        console.warn(`No available bays found for location ${locationId}`);
        return null;
      }

      const lotNumber = randomLot.lotNumber;
      const lotId = randomLot.lotId;       // Bay/Variation ID
      const squareItemId = randomLot.squareItemId; // Get Square Item ID
      
      if (!lotNumber || !lotId || !squareItemId) { // Also check for squareItemId
          console.error('Selected random lot is missing required IDs', randomLot);
          return null;
      }

      // Return all necessary IDs
      return { lotNumber, lotId, squareItemId }; 

    } catch (error) {
      console.error(`Error filtering lots for location ${locationId}:`, error);
      return null;
    }
  };

  const filterBookedLotsByLocationId = (locationId, lotsById) => {
    const bookedLots = lotsById[locationId] || [];
    return bookedLots;
  };

  const getRandomAvailableLot = (lots) => {
    if (!Array.isArray(lots) || lots.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * lots.length);
    return lots[randomIndex];
  };

  const getLotIdByNumber = (lotNumberToFind, lotsBookedById, locationId) => {
      const bookedLotsForLocation = lotsBookedById[locationId] || [];
      const foundLot = bookedLotsForLocation.find(bay => bay.lotNumber === lotNumberToFind);
      
      if (foundLot) {
          return foundLot.lotId;
      } else {
        console.warn(`Lot number ${lotNumberToFind} not found in booked lots for location ${locationId}`);
        return null;
      }
  };

  const returnToStateCards = async () => {
    try {
      setShowStateCards(true);
      setShowPricingCards(false);
      setShowBookingModal(false);
      setBookingSubmitted(false);
      setContent('home');
      setTitle('Secure & Flexible Parking');
    } catch (error) {
      console.error('Error returning to state cards:', error);
    }
  };

  const contextValue = useMemo(() => ({
        // State values
        availableLots,
        setAvailableLots,
        bookingDetails,
        setBookingDetails,
        selectedLocationId,
        setSelectedLocationId,
        showPricingCards,
        setShowPricingCards,
        showStateCards,
        setShowStateCards,
        showBookingModal,
        setShowBookingModal,
        bookingSource,
        setBookingSource,
        bookingSubmitted,
        setBookingSubmitted,
        lotsAvailableByLocationId,
        setLotsAvailableByLocationId,
        filterBookedLotsByLocationId,
        lotsBookedByLocationId,
        setLotsBookedByLocationId,
        filteredAddresses,
        addresses,
        isAddressesLoading,
        searchQuery,
        setSearchQuery,
        stateFilter,
        setStateFilter,
        pricingOptions,
        gateCode,
        setGateCode,
        message,
        setMessage,
        isInitialLotsLoading,
        isRefreshingLots,
        content,
        setContent,
        contentMap,
        seoContent,
        title,
        setTitle,
        defaultRates,

        // Functions
        getLotIdByNumber,
        getLocationWithRates,
        getAllLocationsWithRates,
        filterLotsByLocationId,
        retrieveLots,
        returnToStateCards,
      }), [
        availableLots,
        bookingDetails,
        selectedLocationId,
        showPricingCards,
        showStateCards,
        showBookingModal,
        bookingSource,
        bookingSubmitted,
        lotsAvailableByLocationId,
        lotsBookedByLocationId,
        filteredAddresses,
        addresses,
        isAddressesLoading,
        searchQuery,
        stateFilter,
        pricingOptions,
        gateCode,
        message,
        isInitialLotsLoading,
        isRefreshingLots,
        content,
        contentMap,
        seoContent,
        title,
        defaultRates,
        retrieveLots,
      ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
